import { useEffect, useMemo, useState } from "react";
import {
  usePagination,
  useSortBy,
  useTable,
  useGlobalFilter,
} from "react-table";
import { useBreakpointValue } from "@chakra-ui/react";

const useDataTable = ({
  columns,
  mobileHiddenColumns = [],
  desktopHiddenColumns = ["mobile"],
  data,
  formMethods,
  initialState = {},
  setter = null,
  isLoading,
}) => {
  const isLargerThanLg = useBreakpointValue({ base: false, lg: true });
  const watchSearch = formMethods?.watch("search");
  const [pageCount, setPageCount] = useState(initialState?.pageCount || 0);

  const {
    setHiddenColumns,
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    allColumns,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, sortBy },
  } = useTable(
    {
      columns,
      data,
      initialState: {
        hiddenColumns: desktopHiddenColumns,
        pageIndex: 0,
        ...initialState,
      },
      manualPagination: true,
      pageCount: pageCount,
      autoResetPage: false,
      autoResetSortBy: false,
      disableMultiSort: false,
      autoResetExpanded: false,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const sortOptions = useMemo(() => allColumns.filter((c) => c.canSort), [
    allColumns,
  ]);

  useEffect(() => {
    if (isLargerThanLg) setHiddenColumns(desktopHiddenColumns);
    else setHiddenColumns(mobileHiddenColumns);
  }, [isLargerThanLg, setHiddenColumns]);

  useEffect(() => {
    setGlobalFilter(watchSearch?.split(/ +/));
  }, [setGlobalFilter, watchSearch]);

  useEffect(() => {
    setPageCount(initialState?.pageCount);
  }, [initialState?.pageCount]);

  useEffect(() => {
    if (setter) {
      setter.setCurrentPage(pageIndex + 1);
      setter.setLimit(pageSize);
    }
  }, [pageIndex, pageSize, sortBy]);

  return {
    pagination: {
      gotoPage,
      setPageSize,
      nextPage,
      previousPage,
      canNextPage,
      canPreviousPage,
      pageIndex,
      pageOptions,
      pageSize,
      pageCount: initialState?.pageCount || pageCount,
    },
    getTableProps,
    body: {
      pages: page,
      isLoading,
      prepareRow,
      getTableBodyProps,
      nbColumns: columns.length,
    },
    header: {
      groups: headerGroups,
    },
    sort: {
      options: sortOptions,
      sortBy,
    },
  };
};

export default useDataTable;
